import { createApi } from '@reduxjs/toolkit/query/react';

import { apiGocardless } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { paymentBatchesApi } from '~direct-debits/actions/paymentBatches';
import {
  GocardlessRefund,
  RefundCreateRequest,
  ReversalCreateRequest,
  ReversalResult,
} from '~direct-debits/models';

// `batch_id` rides along on the mutation args purely so we can invalidate the
// batch-detail cache after the call — it is NOT sent in the request body.
interface CreateRefundArgs extends RefundCreateRequest {
  batch_id: string;
}

interface ReversePaymentArgs extends ReversalCreateRequest {
  batch_id: string;
}

// Refunds/reversals live under /gocardless/{refunds,reversals}, a different
// base from payment-batches, so they get their own api slice. They mutate a
// payment line, which the batch-detail + list views render — invalidate those
// tags on the paymentBatchesApi slice so the detail refetches (the line's
// status flips to cancelled on reverse; a refund chip appears on refund) and
// the list stats recompute.
const invalidateBatch = (batchId: string) =>
  paymentBatchesApi.util.invalidateTags([
    { type: 'payment-batch-detail' as const, id: batchId },
    { type: 'payment-batches' as const, id: 'LIST' },
  ]);

export const refundsApi = createApi({
  reducerPath: 'directDebitsRefundsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiGocardless }),
  endpoints: (builder) => ({
    createRefund: builder.mutation<GocardlessRefund, CreateRefundArgs>({
      query: ({ payment_batch_item_id, reason }) => ({
        url: '/refunds',
        method: 'POST',
        body: { payment_batch_item_id, reason },
      }),
      transformResponse: (payload) => unwrap<GocardlessRefund>(payload),
      async onQueryStarted({ batch_id }, { dispatch, queryFulfilled }) {
        // Only invalidate on success. The try/catch is required: an uncaught
        // `queryFulfilled` rejection here surfaces as an RTK "unhandled error"
        // log on every failed mutation. The component's `.unwrap()` handles the
        // user-facing error toast.
        try {
          await queryFulfilled;
          dispatch(invalidateBatch(batch_id));
        } catch {
          // Mutation failed — nothing to invalidate.
        }
      },
    }),
    reversePayment: builder.mutation<ReversalResult, ReversePaymentArgs>({
      query: ({ payment_batch_item_id, reason }) => ({
        url: '/reversals',
        method: 'POST',
        body: reason ? { payment_batch_item_id, reason } : { payment_batch_item_id },
      }),
      transformResponse: (payload) => unwrap<ReversalResult>(payload),
      async onQueryStarted({ batch_id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateBatch(batch_id));
        } catch {
          // Mutation failed — nothing to invalidate.
        }
      },
    }),
  }),
});

export const { useCreateRefundMutation, useReversePaymentMutation } = refundsApi;
